/**
 * Daily affirmation — a data-grounded morning note, generated ON DEVICE.
 *
 * Design principles (deliberate, please keep):
 * - EARNED, not generic. Every message cites the user's own evidence
 *   (completions, zone shares, rollovers). ZoneIn's users are senior
 *   professionals and neurodivergent minds alike — hollow praise reads as
 *   patronizing to the first group and lands worse than nothing for the
 *   second. A mirror beats a cheer.
 * - HONEST on rough weeks. Heavy rollovers switch the register from
 *   celebration to companionship: rollovers are information, not failure.
 * - LOCAL and FREE. Pure templates over real stats: no AI quota, no
 *   network, nothing leaves the device, and it can never hallucinate or
 *   misjudge tone on a fragile morning.
 * - DETERMINISTIC per day. The same day always shows the same message
 *   (stable across re-renders and reopens); variety comes from the date.
 */

import { FATIGUE_THRESHOLD } from '../types';

export interface AffirmationFacts {
  /** Tasks completed so far in the CURRENT week (archive length). */
  completedThisWeek: number;
  /** Open (not-done) tasks on the board right now. */
  openCount: number;
  /** Open tasks that have rolled over at least once. */
  rolledOver: number;
  /** Highest rollover count among open tasks (0 when none). */
  maxRollover: number;
  /** Most recent CLOSED week, if any (weekHistory's last record). */
  lastWeek?: {
    completedCount: number;
    rolledOverCount: number;
    /** Percentages 0–100 from the week record's distribution. */
    zone2Share: number;
    zone4Share: number;
  };
  /** Local date key, e.g. "2026-07-27" — seeds the daily pick. */
  dateKey: string;
}

/** Tiny deterministic string hash (FNV-1a) — stable across sessions. */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Pick today's affirmation. Pure and total: any facts produce a message.
 * The candidate pool is built from what the data actually supports, then
 * the date picks one — so a given day is stable, and days differ.
 */
export function dailyAffirmation(facts: AffirmationFacts): string {
  const pool: string[] = [];
  const lw = facts.lastWeek;

  // --- Rough-week register: honesty first, and nothing else that day. ---
  // A board carrying real fatigue should never open with confetti.
  if (facts.maxRollover >= FATIGUE_THRESHOLD || facts.rolledOver >= 3) {
    const n = facts.rolledOver;
    pool.push(
      `${n === 1 ? 'One task keeps' : `${n} tasks keep`} rolling forward. That's information, not failure — it usually means too big, not too weak. The Wrap-Up (tap Start New Week) can help you shrink, hand off, or let go.`,
      `Carrying ${n === 1 ? 'a task' : `${n} tasks`} week to week is a signal worth reading, not a verdict on you. Today, pick just one and make it smaller.`,
      `A rolled-over task is a question, not a debt: is it too big, is it yours, does it still matter? Answering any one of those today counts as progress.`,
    );
    return pool[hash(facts.dateKey) % pool.length];
  }

  // --- Evidence register: cite their own numbers back to them. ---
  if (lw && lw.completedCount > 0) {
    pool.push(
      `Last week you closed ${lw.completedCount} ${lw.completedCount === 1 ? 'task' : 'tasks'}. That's not luck — that's you running a system.`,
    );
    if (lw.zone2Share >= 40) {
      pool.push(
        `${lw.zone2Share}% of last week's completed work was Strategic & Growth. Most people never protect that time — you did.`,
      );
    }
    if (lw.zone4Share <= 10) {
      pool.push(
        `Only ${lw.zone4Share}% of last week went to Time Drains & Diversions. You're reclaiming your own hours, week by week.`,
      );
    }
    if (lw.rolledOverCount === 0) {
      pool.push(
        `You ended last week with nothing rolling over. A clean close is rare — notice what you did right and keep it.`,
      );
    }
  }
  if (facts.completedThisWeek > 0) {
    pool.push(
      `${facts.completedThisWeek} ${facts.completedThisWeek === 1 ? 'task' : 'tasks'} already done this week. Momentum is evidence — you're building it.`,
    );
  }
  if (facts.openCount > 0) {
    pool.push(
      `${facts.openCount} open ${facts.openCount === 1 ? 'task, and it has' : 'tasks, each with'} a zone and a place. What's out of your head can't overwhelm you the same way.`,
    );
  }

  // --- Welcome register: nothing to cite yet — ground it in the practice. ---
  if (pool.length === 0) {
    pool.push(
      `A clear board is a starting line, not an empty page. Capture what's on your mind and let the zones carry the weight.`,
      `You showed up to plan before the day planned you. That's the whole discipline — everything else is detail.`,
    );
  }

  return pool[hash(facts.dateKey) % pool.length];
}

/** Local calendar date key (device timezone) — the card's once-a-day gate. */
export function localDateKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
